import {
	DATA_TYPE_VALUES,
	DetectedPdfField,
	PdfFieldType,
	RequiredField,
} from '@/interfaces';
import { PdfFormField, PdfFormFieldType } from '@/lib/matchPdfFormFields';
import { ClientSignDataType } from './mockDataTypes';

const KNOWN_DATA_TYPES = new Set<string>(DATA_TYPE_VALUES);

const NON_FILLABLE_FIELD_TYPES = new Set<PdfFieldType>(['button', 'unknown']);

export const isFillableFieldType = (fieldType: PdfFieldType): boolean =>
	!NON_FILLABLE_FIELD_TYPES.has(fieldType);

const fieldTypeFromBackend = (fieldType: PdfFormFieldType): PdfFieldType => {
	switch (fieldType) {
		case 'PDFTextField':
			return 'text';
		case 'PDFSignature':
			return 'signature';
		case 'PDFDropdown':
			return 'dropdown';
		case 'PDFOptionList':
			return 'listbox';
		case 'PDFCheckBox':
			return 'checkbox';
		case 'PDFRadioGroup':
			return 'radio';
		case 'PDFButton':
			return 'button';
		default:
			return 'unknown';
	}
};

const firstStringValue = (value: PdfFormField['value']): string => {
	if (Array.isArray(value)) return value[0] ?? '';
	if (typeof value === 'string') return value;
	return '';
};

const normalizedFieldValue = (value: PdfFormField['value']): string => {
	if (Array.isArray(value)) {
		return value
			.map((item) => (item == null ? '' : String(item).trim()))
			.filter(Boolean)
			.join('');
	}
	if (typeof value === 'boolean') return value ? 'true' : '';
	if (typeof value === 'string') return value.trim();
	if (value == null) return '';
	return String(value).trim();
};

/**
 * Text, signature, and checkbox answers are already painted on the page.
 * Dropdown, listbox, and radio stay interactive even when a choice is selected.
 */
const fieldAlreadyHasValue = (
	field: PdfFormField,
	fieldType: PdfFieldType
): boolean => {
	if (
		fieldType === 'dropdown' ||
		fieldType === 'listbox' ||
		fieldType === 'radio'
	) {
		return false;
	}
	const value = normalizedFieldValue(field.value);
	if (!value) return false;
	if (fieldType === 'checkbox') return value.toLowerCase() !== 'off';
	return true;
};

const defaultValueFor = (
	field: PdfFormField,
	fieldType: PdfFieldType,
	exportValue: string | undefined
): string | undefined => {
	const value = firstStringValue(field.value);
	if (fieldType === 'radio') {
		return exportValue && value === exportValue ? exportValue : undefined;
	}
	if (fieldType === 'dropdown' || fieldType === 'listbox') {
		return value.trim() ? value : undefined;
	}
	return undefined;
};

/**
 * Maps `extracted.form.fields` to `DetectedPdfField`s in PDF user space
 * (bottom-left origin). Returns `null` when any field lacks positions so the
 * caller can fall back to client-side detection.
 */
export const fieldsFromExtracted = (
	formFields: PdfFormField[] | undefined,
	requiredFields: RequiredField[] = []
): DetectedPdfField[] | null => {
	if (!formFields || formFields.length === 0) return null;
	if (formFields.some((field) => !field.positions?.length)) return null;

	const clientFieldNames = new Set(
		requiredFields.map((required) => required.field_name)
	);

	return formFields.flatMap((field) => {
		const fieldType = fieldTypeFromBackend(field.fieldType);
		if (!isFillableFieldType(fieldType)) return [];
		if (
			fieldAlreadyHasValue(field, fieldType) &&
			!clientFieldNames.has(field.fieldName)
		) {
			return [];
		}
		const options = field.options?.length
			? field.options.map((option) => ({ label: option, value: option }))
			: undefined;

		return (field.positions ?? []).map((position, widgetIndex) => {
			const exportValue =
				fieldType === 'radio' ? field.options?.[widgetIndex] : undefined;
			const pageIndex = position.pageNum - 1;
			return {
				id: `${pageIndex}-${field.fieldName}-${widgetIndex}`,
				fieldName: field.fieldName,
				label: field.fieldName,
				fieldType,
				pageIndex,
				rect: {
					x: position.x,
					y: position.y,
					width: position.width,
					height: position.height,
				},
				options: fieldType === 'radio' ? undefined : options,
				defaultValue: defaultValueFor(field, fieldType, exportValue),
				exportValue,
			};
		});
	});
};

const requiredByFieldName = (
	requiredFields: RequiredField[]
): Map<string, RequiredField> =>
	new Map(requiredFields.map((required) => [required.field_name, required]));

/** Uses the lawyer-configured `question` as the label, else `fieldName`. */
export const applyRequiredFieldLabels = (
	fields: DetectedPdfField[],
	requiredFields: RequiredField[]
): DetectedPdfField[] => {
	const byName = requiredByFieldName(requiredFields);
	return fields.map((field) => {
		const required = byName.get(field.fieldName);
		const question = required?.question?.trim();
		const isSignature =
			field.fieldType === 'signature' || String(required?.type) === 'SIGNATURE';
		if (!question && !isSignature) return field;
		return {
			...field,
			...(question ? { label: question } : {}),
			...(isSignature ? { fieldType: 'signature' as const } : {}),
		};
	});
};

/**
 * Answers the client already submitted. They prefill the overlay and stay
 * editable when the lawyer sends the document back.
 */
export const previousResponsesFromRequiredFields = (
	requiredFields: RequiredField[]
): Record<string, string> =>
	requiredFields.reduce<Record<string, string>>((acc, required) => {
		const response = required.response?.trim();
		if (response) acc[required.field_name] = response;
		return acc;
	}, {});

export const questionIdsFromRequiredFields = (
	requiredFields: RequiredField[]
): Record<string, string> =>
	requiredFields.reduce<Record<string, string>>((acc, required) => {
		acc[required.field_name] = required.question_id;
		return acc;
	}, {});

export const dataTypesFromRequiredFields = (
	requiredFields: RequiredField[]
): Record<string, ClientSignDataType> =>
	requiredFields.reduce<Record<string, ClientSignDataType>>((acc, required) => {
		const type = String(required.type);
		acc[required.field_name] = KNOWN_DATA_TYPES.has(type)
			? (type as ClientSignDataType)
			: 'DO_NOT_VALIDATE';
		return acc;
	}, {});
